import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Article } from '../types'
import { apiClient } from '../services/api'
import { useApiAuth } from '../hooks/useApiAuth'
import { useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import TabBar from '../components/TabBar'
import SaveArticleForm from '../components/SaveArticleForm'
import ArticleCard from '../components/ArticleCard'
import SearchBar from '../components/SearchBar'
import ExtensionBanner from '../components/ExtensionBanner'
import { useExtensionPrompt } from '../hooks/useExtensionPrompt'

type Filter = 'all' | 'favorites'

/** Matches the server default: how many ranked results one search page holds.
 *  The count shown to the user comes from the response's `totalMatches`, not
 *  from this — the page length used to be all the client had, so a full page
 *  was rendered as "50+" whether the library held 50 matches or 400. */
const SEARCH_LIMIT = 50

/** The parts of a search response that are ABOUT the search rather than its
 *  results. Held as one object so they cannot drift out of step with each other
 *  or with the results they describe — each is only meaningful alongside the
 *  page it came back with. `null` outside search mode and while one is pending.
 */
type SearchMeta = {
  /** Matches before the page cut, so `total > articles.length` means more exist.
   *  `null` when the server did not send one — a backend older than this field. */
  total: number | null
  /** The corpus read hit its cap: the OLDEST articles were not searched. */
  truncated: boolean
  /** The query had no searchable words. Zero results mean "cannot search for
   *  this", not "not in your library" — a distinction the user acts on, by
   *  retyping the same thing rather than trying different words. */
  unsupported: boolean
}

/**
 * The result-count sentence.
 *
 * `total === null` means the server sent no `totalMatches` — a backend older
 * than that field, which is a real state during a deploy and permanent for any
 * client pointed at one. Falling back to the page length there would claim an
 * exactness we do not have ("50 results" over a page that was cut at 50), so
 * that case keeps the older, vaguer "50+" rendering. Vague is not the same as
 * wrong, and only one of the two is a lie.
 */
function resultCountSentence(
  total: number | null,
  shown: number,
  query: string,
  tag: string | null
): string {
  const inTag = tag ? ` in #${tag}` : ''
  if (total === null) {
    const label = shown >= SEARCH_LIMIT ? `${SEARCH_LIMIT}+` : String(shown)
    return `${label} ${shown === 1 ? 'result' : 'results'} for “${query}”${inTag}. `
  }
  // Only when the page is genuinely a subset — saying it on a complete result
  // set invents a limitation the user has not hit.
  const more = total > shown ? `, showing the top ${shown}` : ''
  return `${total} ${total === 1 ? 'result' : 'results'} for “${query}”${inTag}${more}. `
}

/**
 * Builds the query parameters for listArticles.
 * `tags` is sent as a single string (the API filters by exact tag match);
 * it is not an array because the backend expects a scalar query parameter.
 */
function buildFilterParams(filter: Filter, tag: string | null, nextToken?: string) {
  const tagParam = tag ? { tag } : {}
  const base = filter === 'favorites' ? { isFavorite: true } : {}
  return { ...base, ...tagParam, ...(nextToken ? { nextToken } : {}) }
}

export default function ArticlesPage() {
  const { ready: authReady } = useApiAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const sharedUrl = useMemo(() => searchParams.get('share_url') || '', [searchParams])
  // The library is the landing page, so this is also where a visit gets counted.
  const extensionPrompt = useExtensionPrompt()

  const [articles, setArticles] = useState<Article[]>([])
  const initialFilter = useMemo(() => {
    const f = searchParams.get('filter')
    return f === 'favorites' ? (f as Filter) : 'all'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [filter, setFilter] = useState<Filter>(initialFilter)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nextToken, setNextToken] = useState<string | undefined>()
  const [loadingMore, setLoadingMore] = useState(false)

  // What was actually searched for, DERIVED from the URL rather than mirrored
  // into state. That direction is the whole point: state seeded once at mount
  // and written back forever after meant any navigation that dropped `q` — the
  // browser Back button included — was silently undone by the sync effect that
  // put it back. With the URL authoritative, Back, a deep link, and a reload are
  // all the same code path, and there is nothing left to keep in step.
  //
  // Trimmed on read because the submit path trims too. Untrimmed, `?q=%20%20%20`
  // put the page in search mode while the server stripped it to nothing and
  // answered with the plain list — the entire library relabelled as results.
  //
  // A URL carrying both ?q= and ?filter=favorites is unresolvable, since the
  // server rejects that combination outright: Favorites wins and `q` is dropped,
  // rather than issuing a request we know will 400.
  const submittedQuery = useMemo(() => {
    if (filter === 'favorites') return ''
    return (searchParams.get('q') || '').trim()
  }, [searchParams, filter])

  const searchMode = submittedQuery !== ''

  // What is in the box. Changes on every keystroke, and is separate from
  // `submittedQuery` because the results, the count, and the correction line all
  // belong to the query that was submitted, not to whatever is currently typed.
  const [queryInput, setQueryInput] = useState(submittedQuery)
  const [corrections, setCorrections] = useState<Record<string, string> | null>(null)
  const [searchMeta, setSearchMeta] = useState<SearchMeta | null>(null)
  const [searching, setSearching] = useState(false)
  const searchAbortRef = useRef<AbortController | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Bumped on every submit so that re-submitting an UNCHANGED query still
  // re-runs. Without it the submit wrote a value the URL already held, React
  // bailed out of the render, and the user got no request and no acknowledgement
  // at all — after a failed search, pressing Enter again did nothing.
  const [submitSeq, setSubmitSeq] = useState(0)

  // One counter for every reader of `articles`. A per-path AbortController
  // cannot order writes against a DIFFERENT path, and that was the bug: only
  // runSearch could be cancelled, so a slow library fetch landed on top of a
  // fast search and rendered the whole unfiltered list under "N results for X".
  const requestIdRef = useRef(0)

  /** Write the submitted query to the URL, which is what actually runs a search. */
  const commitQuery = useCallback(
    (q: string) => {
      const next = new URLSearchParams(searchParams)
      if (q) next.set('q', q)
      else next.delete('q')
      // Push only when ENTERING search, so Back leaves it. Refinements and the
      // exit replace, so Back never walks through a trail of half-typed queries.
      const entering = submittedQuery === '' && q !== ''
      setSearchParams(next, { replace: !entering })
    },
    [searchParams, setSearchParams, submittedQuery]
  )

  // Keep the box in step when the query changes from OUTSIDE the box: a deep
  // link, browser Back, or exitSearch. Typing is unaffected, since that moves
  // `queryInput` without touching `submittedQuery`.
  //
  // `corrections` is retired here rather than in exitSearch because the URL is
  // authoritative, so there are TWO ways out of search mode and exitSearch is
  // only one of them. Clearing it there left a Back-exit holding the previous
  // query's "Showing results for X", which then rendered over the next search
  // while it was still pending — an affordance bound to the click instead of to
  // the state, which is the whole bug family this file was rewritten to remove.
  // Safe to clear before runSearch writes the new value: this effect runs first.
  useEffect(() => {
    setQueryInput(submittedQuery)
    setCorrections(null)
    // Retired with `corrections`, and for the same reason: it describes the
    // PREVIOUS query, so leaving it up captions the next search's pending state
    // with the last one's count and warnings.
    setSearchMeta(null)
  }, [submittedQuery])
  const [toast, setToast] = useState<{ message: string; onUndo?: () => void } | null>(null)
  const pollingIntervalsRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set())
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Pending optimistic deletes, keyed by articleId, awaiting their grace period
  // before the API call commits. Undo cancels the matching timer.
  const deleteTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const fetchArticles = useCallback(async (currentFilter: Filter, tag: string | null) => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await apiClient.listArticles(buildFilterParams(currentFilter, tag))
      if (requestId !== requestIdRef.current) return
      setArticles(result.articles)
      setNextToken(result.nextToken)
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load articles')
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [])

  const runSearch = useCallback(async (query: string, tag: string | null) => {
    // Discard the in-flight request before starting another. Submit-driven
    // search makes this rare, but two fast Enters still race, and the loser
    // rendering over the winner is exactly the bug this prevents. The abort
    // stops the request; the shared requestId is what orders this write against
    // the OTHER paths that write `articles`.
    searchAbortRef.current?.abort()
    const controller = new AbortController()
    searchAbortRef.current = controller
    const requestId = ++requestIdRef.current

    setSearching(true)
    setError(null)
    try {
      const result = await apiClient.listArticles(
        { q: query, limit: SEARCH_LIMIT, ...(tag ? { tag } : {}) },
        controller.signal
      )
      if (controller.signal.aborted || requestId !== requestIdRef.current) return
      setArticles(result.articles)
      setCorrections(result.corrections ?? null)
      setSearchMeta({
        // `null`, not a fallback count: see resultCountSentence. `?? null` also
        // guards the 0 case, which `||` would misread as absent.
        total: result.totalMatches ?? null,
        truncated: result.truncated === true,
        unsupported: result.unsupportedQuery === true,
      })
      setNextToken(undefined) // a search response has no nextToken, by design
    } catch (err: unknown) {
      if (controller.signal.aborted || requestId !== requestIdRef.current) return
      setArticles([])
      setCorrections(null)
      setSearchMeta(null)
      // Distinguishable from the empty state on purpose: "No results for X"
      // when the server actually failed is a lie the user acts on by retyping.
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      // `searching` belongs to THIS request, so it is released on the
      // controller, never on the shared counter. Guarding it with requestId was
      // a real bug: a search superseded by a URL-driven exit (browser Back
      // dropping `q`) bumps requestIdRef via fetchArticles but never aborts, so
      // the finally skipped setSearching(false) and the spinner stayed up
      // forever. That is the A1 spinner bug reintroduced one variable over.
      if (!controller.signal.aborted) {
        setSearching(false)
        // `loading` IS shared with fetchArticles, so it keeps the counter guard.
        // Search owns it too: it was initialised true and cleared only by
        // fetchArticles, which this branch skips — so a search that started from
        // the URL left the list on a spinner under a status line saying results
        // existed.
        if (requestId === requestIdRef.current) setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!authReady) return
    if (searchMode) {
      runSearch(submittedQuery, activeTag)
    } else {
      fetchArticles(filter, activeTag)
    }
    // submitSeq is a dependency so that re-submitting an unchanged query re-runs.
  }, [
    filter,
    activeTag,
    fetchArticles,
    authReady,
    searchMode,
    submittedQuery,
    runSearch,
    submitSeq,
  ])

  useEffect(() => {
    const controller = searchAbortRef
    return () => controller.current?.abort()
  }, [])

  // NOTE: there is deliberately no state->URL sync effect here any more. The URL
  // is the source of truth (see `submittedQuery` above) and writes go through
  // commitQuery, so there is only one direction and nothing to reconcile. The
  // effect this replaced re-added `q` whenever it disagreed with state, which is
  // why browser Back could never leave search mode.

  function exitSearch() {
    // Outside search mode this must not touch shared state. It used to clear
    // `error` unconditionally, but nothing refetches when `searchMode` does not
    // change — so a failed library load plus one keystroke and a click on the ✕
    // became "No articles yet", taking the Retry button with it.
    if (!searchMode) {
      setQueryInput('')
      return
    }
    searchAbortRef.current?.abort()
    setQueryInput('')
    commitQuery('')
    // `corrections` is not cleared here — the submittedQuery effect above owns
    // it, so both exits behave identically.
    setSearching(false)
    // Safe to clear `error` here, and necessary: we are past the early return,
    // so any error still set is a SEARCH error being abandoned. fetchArticles
    // does clear it on entry, but it runs in a passive effect one commit later —
    // in that gap searchMode is false and error is still set, which renders
    // "Something went wrong" for a frame. The original unconditional clear was
    // wrong only because it also ran OUTSIDE search mode, where nothing
    // refetches; the early return above now covers that case.
    setError(null)
  }

  /** Leave search mode and put the caret back where the user was. */
  function exitSearchAndRefocus() {
    exitSearch()
    searchInputRef.current?.focus()
  }

  // Switching tabs must not leave stale search results rendered under a tab
  // that cannot produce them. The active tag survives, exactly as today.
  function handleFilterChange(next: Filter) {
    if (next !== filter && searchMode) exitSearch()
    setFilter(next)
  }

  useEffect(() => {
    const intervals = pollingIntervalsRef.current
    const deleteTimers = deleteTimersRef.current
    return () => {
      intervals.forEach(clearInterval)
      deleteTimers.forEach(clearTimeout)
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    }
  }, [])

  function showToast(message: string, onUndo?: () => void) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToast({ message, onUndo })
    toastTimerRef.current = setTimeout(() => setToast(null), 5000)
  }

  function hideToast() {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToast(null)
  }

  async function loadMore() {
    // `searchMode` guard: a search response is a single ranked page and
    // buildFilterParams never carries `q`, so an in-flight page 2 would append
    // the next CHRONOLOGICAL page beneath the ranked results and restore
    // nextToken. Captures rather than bumps requestId — this appends to a result
    // set instead of replacing one, so it must be discarded if anything else
    // took ownership meanwhile, not take ownership itself.
    if (!nextToken || loadingMore || searchMode) return
    const requestId = requestIdRef.current
    setLoadingMore(true)
    try {
      const result = await apiClient.listArticles(buildFilterParams(filter, activeTag, nextToken))
      if (requestId !== requestIdRef.current) return
      setArticles((prev) => [...prev, ...result.articles])
      setNextToken(result.nextToken)
    } catch (err: unknown) {
      console.error('Failed to load more articles:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  function pollForExcerpt(articleId: string) {
    const MAX_ATTEMPTS = 8 // 8 × 2.5s = 20s max
    let attempts = 0
    const interval = setInterval(async () => {
      attempts++
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(interval)
        pollingIntervalsRef.current.delete(interval)
        return
      }
      try {
        const updated = await apiClient.getArticle(articleId)
        if (updated.excerpt) {
          setArticles((prev) =>
            prev.map((a) => (a.articleId === articleId ? { ...a, excerpt: updated.excerpt } : a))
          )
          clearInterval(interval)
          pollingIntervalsRef.current.delete(interval)
        }
      } catch {
        // transient error — keep trying
      }
    }, 2500)
    pollingIntervalsRef.current.add(interval)
  }

  function handleSaved(article: Article) {
    // Not in search mode: a save was never scored against the query, so
    // prepending it puts a non-result at rank #1 of a ranked list and makes the
    // "N results for X" count include it. The save still happened; it appears
    // when the user leaves search.
    if (!searchMode) {
      setArticles((prev) => [article, ...prev])
    } else {
      // Skipping the prepend removes the only feedback the save had, so it has
      // to be replaced rather than just dropped: otherwise the button does
      // nothing observable and the user saves the same URL again.
      showToast('Saved — clear the search to see it')
    }
    // Clear the share param after saving — and ONLY that one. Passing {} wiped
    // every param, including `filter` and the active query.
    if (sharedUrl) {
      const next = new URLSearchParams(searchParams)
      next.delete('share_url')
      setSearchParams(next, { replace: true })
    }
    // If no excerpt yet, poll until the async summarizer fills it in
    if (!article.excerpt) {
      pollForExcerpt(article.articleId)
    }
  }

  function handleUpdate(updated: Article) {
    setArticles((prev) => prev.map((a) => (a.articleId === updated.articleId ? updated : a)))
  }

  // Optimistically remove the article and offer an Undo toast. The API delete
  // only commits after a grace period, so Undo can cancel it before it fires.
  function scheduleDelete(article: Article) {
    const index = articles.findIndex((a) => a.articleId === article.articleId)
    if (index === -1) return

    setArticles((prev) => prev.filter((a) => a.articleId !== article.articleId))

    const restore = () =>
      setArticles((prev) => {
        if (prev.some((a) => a.articleId === article.articleId)) return prev
        const next = [...prev]
        next.splice(Math.min(index, next.length), 0, article)
        return next
      })

    const timer = setTimeout(async () => {
      deleteTimersRef.current.delete(article.articleId)
      try {
        await apiClient.deleteArticle(article.articleId)
      } catch (err: unknown) {
        console.error('Failed to delete article:', err)
        restore()
        showToast("Couldn't delete article")
      }
    }, 5000)
    deleteTimersRef.current.set(article.articleId, timer)

    showToast('Deleted', () => {
      const t = deleteTimersRef.current.get(article.articleId)
      if (t) {
        clearTimeout(t)
        deleteTimersRef.current.delete(article.articleId)
      }
      restore()
    })
  }

  function handleTagClick(tag: string) {
    setActiveTag(tag)
  }

  const emptyMessages: Record<Filter, { title: string; body: string }> = {
    all: { title: 'No articles yet', body: 'Paste a URL above to save your first article.' },
    favorites: { title: 'No favorites yet', body: 'Star an article to add it to favorites.' },
  }

  return (
    <>
      <Header />
      <main className="page">
        {extensionPrompt.visible && (
          <ExtensionBanner
            onDismiss={extensionPrompt.dismiss}
            onAcknowledge={extensionPrompt.acknowledge}
          />
        )}

        <SaveArticleForm onSaved={handleSaved} initialUrl={sharedUrl} />

        <TabBar
          filter={filter}
          onFilterChange={handleFilterChange}
          activeTag={activeTag}
          onClearTag={() => setActiveTag(null)}
        />

        {/* Saved only. On Favorites it is ABSENT rather than disabled — a
            disabled field invites the user to work out why. */}
        {filter === 'all' && (
          <SearchBar
            inputRef={searchInputRef}
            value={queryInput}
            onChange={(v) => {
              setQueryInput(v)
              // The correction line belongs to the response that produced it,
              // not to the search box. Editing the query retires it.
              if (corrections) setCorrections(null)
            }}
            onSubmit={() => {
              const q = queryInput.trim()
              // A no-op submit used to cost nothing: setting state to the value
              // it already held hit React's bail-out. submitSeq removed that, so
              // pressing Enter in an empty box outside search mode refetched the
              // whole library and blanked the list to a spinner.
              if (!q && !searchMode) return
              commitQuery(q)
              // Bump ONLY when the URL will not change. react-router runs
              // setSearchParams inside startTransition (v7_startTransition is on
              // in App.tsx), so it commits on the transition lane while
              // setSubmitSeq lands on the urgent one. Doing both fires the data
              // effect twice — two requests per submit in production. Tests
              // cannot see it: MemoryRouter defaults the flag off, which is
              // exactly why the test harness below now sets it explicitly.
              if (q === submittedQuery) setSubmitSeq((n) => n + 1)
            }}
            onClear={exitSearch}
            active={searchMode}
            pending={searching}
          />
        )}

        {/* The live region is mounted UNCONDITIONALLY, and `searchMode` gates its
            contents instead. Mounting a region at the same moment as the text
            inside it is the classic way to announce nothing: the accessibility
            tree gets the node and its content in one mutation, so there is no
            "change" to report and the first search of a session is silent. The
            div is empty and collapsed (`:empty` in App.css) the rest of the time.
            `aria-atomic` so the count is re-read as a whole sentence rather than
            as whichever words happened to change. */}
        <div className="search-status" role="status" aria-live="polite" aria-atomic="true">
          {searchMode && (
            <>
              {corrections && (
                <p className="search-correction">
                  Showing results for {Object.values(corrections).join(', ')}
                </p>
              )}
              {/* The library was too large to read in full, so this search did
                  not cover all of it. Said out loud because the read is
                  newest-first: what was skipped is the OLDEST articles, which
                  is precisely what someone searching a read-later library is
                  looking for. Silence here turns "we did not look everywhere"
                  into "it is not there". */}
              {searchMeta?.truncated && (
                <p className="search-warning">
                  Your library is too large to search all at once — only your most recent articles
                  were searched.
                </p>
              )}
              <p className="search-count">
                {searching ? (
                  'Searching… '
                ) : error ? (
                  'Search failed. '
                ) : searchMeta?.unsupported ? (
                  // Not "0 results for 你好". This is the announced line, so
                  // saying a count here would tell a screen-reader user the
                  // library was searched and came back empty — the exact claim
                  // the empty state below exists to stop making.
                  <>No searchable words in “{submittedQuery}”. </>
                ) : (
                  resultCountSentence(
                    searchMeta?.total ?? null,
                    articles.length,
                    submittedQuery,
                    activeTag
                  )
                )}
                {/* The second way out, and the discoverable one. The × inside the
                    field is easy to miss, and this line is where you are already
                    looking when you want the whole list back.

                    Rendered in ALL THREE status states, never inside the pending
                    branch: while a slow search was in flight this used to vanish,
                    leaving the easy-to-miss × as the only exit — the shipped bug
                    narrowed to a time window rather than removed.

                    The label names the tag when one is active, because
                    `exitSearch` deliberately PRESERVES `activeTag` — so "Show all
                    articles" promised a list the click does not produce. */}
                <button type="button" className="search-clear-link" onClick={exitSearchAndRefocus}>
                  {activeTag ? `Show all #${activeTag}` : 'Show all articles'}
                </button>
              </p>
            </>
          )}
        </div>

        {/* One spinner branch, not two identical ones. `loading` is now cleared
            by whichever path ran, so this no longer hangs in search mode. */}
        {searching || loading ? (
          <div className="list-loading">
            <div className="spinner" />
          </div>
        ) : error ? (
          /* role="alert" because a failure is the one state a user must not have
             to notice visually. The polite status line above says "Search
             failed"; this carries the reason and the Retry, so it is the part
             worth interrupting for. */
          <div className="empty-state" role="alert">
            <h3>{searchMode ? "Search didn't work" : 'Something went wrong'}</h3>
            <p>{error}</p>
            <button
              className="btn-secondary"
              onClick={() =>
                searchMode ? runSearch(submittedQuery, activeTag) : fetchArticles(filter, activeTag)
              }
              style={{ marginTop: 16 }}
            >
              Retry
            </button>
          </div>
        ) : articles.length === 0 ? (
          <div className="empty-state">
            {searchMode && searchMeta?.unsupported ? (
              /* NOT "No results for 你好". Nothing was looked up: the query held
                 no word the matcher can index, so zero results is a fact about
                 the query, not about the library. Rendering it as an absence is
                 a claim the server never made, and the user acts on it by
                 retyping the same thing. Reached by non-Latin scripts, by `c++`,
                 and by queries that are nothing but common words. */
              <>
                <h3>No searchable words in “{submittedQuery}”</h3>
                <p>
                  Search needs at least one distinctive word. Punctuation, symbols, and non-Latin
                  scripts aren’t supported yet.
                </p>
              </>
            ) : searchMode ? (
              <>
                {/* Name the tag as well as the query. A search inside an active
                    tag that finds nothing is indistinguishable, from this screen,
                    from "you never saved that" — so the user concludes the
                    article is not in their library and stops looking. */}
                <h3>
                  No results for “{submittedQuery}”{activeTag ? ` in #${activeTag}` : ''}
                </h3>
                {activeTag && (
                  <p>
                    Only articles tagged #{activeTag} were searched.{' '}
                    <button
                      type="button"
                      className="search-clear-link"
                      onClick={() => setActiveTag(null)}
                    >
                      Search everything instead
                    </button>
                  </p>
                )}
                {/* Set the expectation honestly: body text is not searched, and
                    tags are not scored, so promising either would be a lie. */}
                <p>Search covers titles, summaries, and topics — not the full article text.</p>
              </>
            ) : (
              <>
                <h3>
                  {activeTag ? `No articles tagged "${activeTag}"` : emptyMessages[filter].title}
                </h3>
                <p>
                  {activeTag
                    ? 'Try a different tag or clear the filter.'
                    : emptyMessages[filter].body}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="article-list">
              {articles.map((article) => (
                <ArticleCard
                  key={article.articleId}
                  article={article}
                  onUpdate={handleUpdate}
                  onDelete={scheduleDelete}
                  onTagClick={handleTagClick}
                />
              ))}
            </div>
            {/* Load-more is hidden in search mode: a search response is a single
                ranked page and carries no nextToken. */}
            {!searchMode && nextToken && (
              <div className="load-more">
                <button className="btn-secondary" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {toast && (
        <div className="undo-toast" role="status">
          <span>{toast.message}</span>
          {toast.onUndo && (
            <button
              className="undo-toast-action"
              onClick={() => {
                toast.onUndo?.()
                hideToast()
              }}
            >
              Undo
            </button>
          )}
        </div>
      )}
    </>
  )
}
